import type { LabelGridOutlet } from '../contracts/labelgrid';

/**
 * Prototype catalogue sourced from LabelGrid's public Supported Stores directory.
 * The live Publisher still replaces this with the account-specific `/distro-outlets`
 * response. YouTube Music is intentionally omitted for the current product rollout.
 */
const outlet = (
  id: number,
  key: string,
  name: string,
  type: string,
  isUgcStore = false
): LabelGridOutlet => ({ id, key, name, type, is_ai_dsp: false, is_ugc_store: isUgcStore });

export const PUBLISHER_PROTOTYPE_OUTLETS: readonly LabelGridOutlet[] = [
  outlet(1, 'spotify', 'Spotify', 'Streaming'),
  outlet(2, 'apple-music', 'Apple Music', 'Streaming'),
  outlet(3, 'amazon-music', 'Amazon Music', 'Streaming'),
  outlet(4, 'tidal', 'TIDAL', 'Streaming'),
  outlet(5, 'deezer', 'Deezer', 'Streaming'),
  outlet(6, 'pandora', 'Pandora', 'Streaming'),
  outlet(7, 'soundcloud', 'SoundCloud', 'Streaming'),
  outlet(8, 'napster', 'Napster', 'Streaming'),
  outlet(9, 'audiomack', 'Audiomack', 'Streaming'),

  outlet(10, 'tiktok-capcut', 'TikTok / CapCut', 'Social & video', true),
  outlet(11, 'meta', 'Instagram / Facebook (Meta)', 'Social & video', true),
  outlet(12, 'youtube-content-id', 'YouTube Content ID', 'Social & video', true),
  outlet(13, 'snapchat', 'Snapchat', 'Social & video', true),
  outlet(14, 'canva', 'Canva', 'Social & video', true),
  outlet(15, 'kuaishou', 'Kuaishou', 'Social & video', true),

  outlet(16, 'kkbox', 'KKBOX', 'Regional'),
  outlet(17, 'awa', 'AWA', 'Regional'),
  outlet(18, 'flo', 'FLO', 'Regional'),
  outlet(19, 'joox', 'Joox', 'Regional'),
  outlet(20, 'jiosaavn', 'JioSaavn', 'Regional'),
  outlet(21, 'netease-cloud-music', 'NetEase Cloud Music', 'Regional'),
  outlet(22, 'qq-music', 'QQ Music', 'Regional'),
  outlet(23, 'kugou', 'Kugou', 'Regional'),
  outlet(24, 'kuwo', 'Kuwo', 'Regional'),
  outlet(25, 'wesing', 'WeSing', 'Regional'),
  outlet(26, 'alibaba-music', 'Alibaba Music', 'Regional'),
  outlet(27, 'anghami', 'Anghami', 'Regional'),
  outlet(28, 'boomplay', 'Boomplay', 'Regional'),

  outlet(29, 'beatport', 'Beatport', 'Electronic & DJ'),
  outlet(30, 'juno-download', 'Juno Download', 'Electronic & DJ'),
  outlet(31, 'mixcloud', 'Mixcloud', 'Electronic & DJ'),
  outlet(32, 'dnb-radio', 'DNB Radio', 'Electronic & DJ'),

  outlet(33, 'itunes-store', 'iTunes Store', 'Download stores'),
  outlet(34, 'amazon-downloads', 'Amazon Music (Downloads)', 'Download stores'),
  outlet(35, '7digital', '7digital', 'Download stores'),
  outlet(36, 'bandcamp', 'Bandcamp', 'Download stores'),

  outlet(37, 'iheartradio', 'iHeartRadio', 'Radio & discovery'),
  outlet(38, 'slacker-radio', 'Slacker Radio', 'Radio & discovery'),
  outlet(39, 'trebel', 'Trebel', 'Radio & discovery'),
  outlet(40, 'turntable', 'Turntable', 'Radio & discovery'),
  outlet(41, 'rythm', 'Rythm', 'Radio & discovery'),

  outlet(42, 'gracenote', 'Gracenote', 'Recognition & rights'),
  outlet(43, 'acrcloud', 'ACRCloud', 'Recognition & rights'),
  outlet(44, 'audible-magic', 'Audible Magic', 'Recognition & rights'),
  outlet(45, 'soundexchange', 'SoundExchange', 'Recognition & rights'),
  outlet(46, 'shazam', 'Shazam', 'Recognition & rights'),

  outlet(47, 'peloton', 'Peloton', 'Fitness & wellness'),
  outlet(48, 'soundtrack-your-brand', 'Soundtrack Your Brand', 'Fitness & wellness'),
  outlet(49, 'pretzel', 'Pretzel', 'Fitness & wellness'),
  outlet(50, 'roxi', 'Roxi', 'Fitness & wellness'),
  outlet(51, 'coda-music', 'Coda Music', 'Fitness & wellness'),
];

export function withoutYouTubeMusic(outlets: readonly LabelGridOutlet[]): LabelGridOutlet[] {
  return outlets.filter(
    (item) =>
      item.key.toLowerCase() !== 'youtube-music' && item.name.toLowerCase() !== 'youtube music'
  );
}

const STANDARD_PUBLISHER_OUTLET_TYPES = new Set(['streaming', 'social & video']);

/** Non-Unlimited Publisher accounts only receive the two core outlet sections. */
export function isPublisherOutletSectionAvailable(
  outlet: LabelGridOutlet,
  hasUnlimitedPlan: boolean
): boolean {
  return hasUnlimitedPlan || STANDARD_PUBLISHER_OUTLET_TYPES.has(outlet.type.trim().toLowerCase());
}

/** Premium outlets that stay visible in the core sections but require OTO5. */
export function publisherOutletRequiresUnlimited(outlet: LabelGridOutlet): boolean {
  const key = outlet.key.trim().toLowerCase();
  const name = outlet.name.trim().toLowerCase();
  return (
    key === 'amazon-music' ||
    key === 'tidal' ||
    key.includes('tiktok') ||
    name === 'amazon music' ||
    name === 'tidal' ||
    name.includes('tiktok')
  );
}

export function isPublisherOutletAvailable(
  outlet: LabelGridOutlet,
  hasUnlimitedPlan: boolean
): boolean {
  return (
    isPublisherOutletSectionAvailable(outlet, hasUnlimitedPlan) &&
    (hasUnlimitedPlan || !publisherOutletRequiresUnlimited(outlet))
  );
}
